"use client";

import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Header from "../Header";
import Footer from "../Footer";

const base_url = process.env.REACT_APP_BASE_URL;

const PrivateAuctionView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [auctionData, setAuctionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!id) {
      setError("No auction ID provided");
      setLoading(false);
      return;
    }

    const fetchAuctionById = async () => {
      const token = localStorage.getItem("token");
      
      if (!token) {
        setError("Authentication required. Please log in.");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError("");
        
        const res = await fetch(`${base_url}/api/auction/getInvitedPrivateAuctionList`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        const json = await res.json();
        
        if (!res.ok) {
          throw new Error(json.message || `HTTP error! status: ${res.status}`);
        }

        if (json.data && Array.isArray(json.data)) {
          // Find the specific auction by ID from the array
          const foundAuction = json.data.find(item => 
            item.auction && item.auction.id.toString() === id.toString()
          );
          
          if (foundAuction) {
            setAuctionData(foundAuction);
          } else {
            throw new Error("Auction not found in your private auctions");
          }
        } else {
          throw new Error("No auction data found in response");
        }
      } catch (err) {
        console.error("Error fetching auction details:", err);
        setError(err.message || "Failed to fetch auction details");
      } finally {
        setLoading(false);
      }
    };

    fetchAuctionById();
  }, [id]);

  // Loading state
  if (loading) {
    return (
      <section className="container sec_pd">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
          <div className="spinner-border text-dark" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </section>
    );
  }

  // Error state
  if (error) {
    return (
      <section className="container sec_pd">
        <div className="mt-5">
          <div className="alert alert-danger" role="alert">
            <h5 className="alert-heading">Error</h5>
            <p>{error}</p>
            <button 
              className="btn btn-outline-danger btn-sm"
              onClick={() => navigate(-1)}
            >
              Go Back
            </button>
          </div>
        </div>
      </section>
    );
  }

  // No auction found
  if (!auctionData || !auctionData.auction) {
    return (
      <section className="container sec_pd">
        <div className="mt-5">
          <div className="alert alert-warning" role="alert">
            <h5 className="alert-heading">No Auction Found</h5>
            <p>The requested auction details could not be found.</p>
            <button 
              className="btn btn-outline-warning btn-sm"
              onClick={() => navigate(-1)}
            >
              Go Back
            </button>
          </div>
        </div>
      </section>
    );
  }

  const { auction } = auctionData;

  // Display value as-is without any formatting
  const displayValue = (value) => {
    if (!value || value === 'null' || value === null) {
      return '';
    }
    return value;
  };

  return (
   <>
   <Header />
   <section className="container sec_pd">
      <div className="mt-5 text-dark mb-5">
        {/* Back Button */}
        <div className="mb-3">
          <button 
            className="btn btn-outline-secondary btn-sm"
            onClick={() => navigate(-1)}
          >
            ← Back
          </button>
        </div>

        {/* Private Auction Notice */}
        <div className="alert alert-warning mb-4">
          <div className="d-flex align-items-center">
            <i className="fas fa-lock me-2"></i>
            <div>
              <strong>Private Auction</strong>
              <p className="mb-0">You have been invited to view this private auction.</p>
            </div>
          </div>
        </div>

        {/* Auction Title */}
        <h3 className="mb-4">{auction.title}</h3>

        {/* Basic Info Section */}
        <div className="border p-4 mb-4">
          <h4 className="mb-3">Basic Information</h4>
          <div className="row">
            <div className="col-md-6 mb-3">
              <strong>Auction Type:</strong>
              <p className="mb-0 text-capitalize">Private</p>
            </div>
          
            {auctionData.createdAt && (
              <div className="col-md-6 mb-3">
                <strong>Invitation Date:</strong>
                <p className="mb-0">{new Date(auctionData.createdAt).toLocaleDateString()}</p>
              </div>
            )}
          </div>
        </div>

        {/* Detailed Information */}
        {auction.details && (
          <div className="border p-4 mb-4">
            <h4 className="mb-4">Auction Details</h4>
            <div className="row">
              {auction.details.description && (
                <div className="col-12 mb-3">
                  <strong>Description:</strong>
                  <p className="mt-2">{displayValue(auction.details.description)}</p>
                </div>
              )}
              
              <div className="col-md-4 mb-3">
                <strong>Artist:</strong>
                <p className="mb-0">{displayValue(auction.details.artist)}</p>
              </div>
              
              <div className="col-md-4 mb-3">
                <strong>Object Type:</strong>
                <p className="mb-0">{displayValue(auction.details.object_type)}</p>
              </div>
              
              <div className="col-md-4 mb-3">
                <strong>Material:</strong>
                <p className="mb-0">{displayValue(auction.details.material)}</p>
              </div>
              
              <div className="col-md-4 mb-3">
                <strong>Origin:</strong>
                <p className="mb-0">{displayValue(auction.details.item_origin)}</p>
              </div>
              
              {auction.details.restrict_material && (
                <div className="col-md-4 mb-3">
                  <strong>Material Restrictions:</strong>
                  <p className="mb-0">{displayValue(auction.details.restrict_material)}</p>
                </div>
              )}
              <div className="col-md-4 mb-3">
                <strong>Measurements units</strong>
                <p className="mb-0">
                {displayValue(auction.details.measurement_units)}
                </p>
              </div>
              
              <div className="col-md-4 mb-3">
                <strong>Height</strong>
                <p className="mb-0">
                  {displayValue(auction.details.height)} 
                </p>
              </div>
              <div className="col-md-4 mb-3">
                <strong>Width</strong>
                <p className="mb-0">
                {displayValue(auction.details.width)}
                </p>
              </div>
              <div className="col-md-4 mb-3">
                <strong>Depth</strong>
                <p className="mb-0">
                {displayValue(auction.details.depth)}
                </p>
              </div>
              
              <div className="col-md-4 mb-3">
                <strong>Ownership History:</strong>
                <p className="mb-0">{displayValue(auction.details.ownership_history)}</p>
              </div>
              
              <div className="col-md-4 mb-3">
                <strong>Appraisals:</strong>
                <p className="mb-0">{displayValue(auction.details.appraisals)}</p>
              </div>
              
              <div className="col-md-4 mb-3">
                <strong>Purchase Price:</strong>
                <p className="mb-0">
                  {displayValue(auction.details.price)} {displayValue(auction.details.currency)}
                </p>
              </div>
              
              <div className="col-md-4 mb-3">
                <strong>Year Acquired:</strong>
                <p className="mb-0">{displayValue(auction.details.year_paid)}</p>
              </div>
              
              <div className="col-md-4 mb-3">
                <strong>Markings:</strong>
                <p className="mb-0">{displayValue(auction.details.markings)}</p>
              </div>
              
              <div className="col-md-4 mb-3">
                <strong>Damage:</strong>
                <p className="mb-0">{displayValue(auction.details.damage)}</p>
              </div>
              
              <div className="col-md-4 mb-3">
                <strong>Repair History:</strong>
                <p className="mb-0">{displayValue(auction.details.repair)}</p>
              </div>
              
              {auction.details.carbon_dating && (
                <div className="col-md-6 mb-3">
                  <strong>Carbon Dating:</strong>
                  <p className="mb-0">{displayValue(auction.details.carbon_dating)}</p>
                </div>
              )}
              
              {auction.details.notes && (
                <div className="col-12 mb-3">
                  <strong>Additional Notes:</strong>
                  <p className="mt-2">{displayValue(auction.details.notes)}</p>
                </div>
              )}
            </div>

            {/* Certificates Section */}
            {(auction.details.certificate1 || auction.details.certificate2) && (
              <div className="mt-4 pt-4 border-top">
                <h5 className="mb-3">Certificates & Documentation</h5>
                <div className="row">
                  {auction.details.certificate1 && (
                    <div className="col-md-6 mb-3">
                      <strong>Certificate 1:</strong><br />
                      <a 
                        href={auction.details.certificate1} 
                        className="btn btn-dark text-warning mt-2" 
                        target="_blank" 
                        rel="noopener noreferrer"
                      >
                        📄 View Certificate 1
                      </a>
                    </div>
                  )}
                  {auction.details.certificate2 && (
                    <div className="col-md-6 mb-3">
                      <strong>Certificate 2:</strong><br />
                      <a 
                        href={auction.details.certificate2} 
                        className="btn btn-dark text-warning mt-2" 
                        target="_blank" 
                        rel="noopener noreferrer"
                      >
                        📄 View Certificate 2
                      </a>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Images Section */}
        {auction.images?.length > 0 && (
          <div className="mb-4">
            <h4 className="mb-3">Gallery ({auction.images.length} images)</h4>
            <div className="row g-3">
              {auction.images
                .sort((a, b) => a.sort_order - b.sort_order) // Sort by sort_order
                .map((image, index) => (
                <div
                  key={image.id || index}
                  className="col-12 col-sm-6 col-md-4 col-lg-4"
                >
                  <div className="position-relative">
                    <img
                      src={image.image}
                      className="img-fluid rounded border shadow-sm"
                      alt={`${image.image_type || 'Auction image'} ${index + 1}`}
                      style={{
                        height: "300px",
                        width: "100%",
                        objectFit: "cover",
                        cursor: "pointer"
                      }}
                      onClick={() => {
                        window.open(image.image, '_blank');
                      }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        console.error('Failed to load image:', image.image);
                      }}
                    />
                    {image.image_type && (
                      <div className="position-absolute bottom-0 start-0 end-0 bg-dark bg-opacity-75 text-white text-center py-1">
                        <small className="text-capitalize">
                          {image.image_type.replace(/_/g, ' ')}
                        </small>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* No content message */}
        {(!auction.details && (!auction.images || auction.images.length === 0)) && (
          <div className="alert alert-info">
            <p className="mb-0">No additional details or images available for this auction.</p>
          </div>
        )}
      </div>
    </section>
   <Footer />
   </>
  );
};

export default PrivateAuctionView;